/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
/**
 * Public OBS Browser-Source overlay.
 * Streamers add https://.../overlay/:sessionId to OBS as a Browser Source.
 * Transparent background, big pulsing visualizer, last-read message overlay.
 *
 * Query params:
 *   ?scale=0.8   — overall UI scale (0.5 .. 2)
 *   ?pos=bl|br|tl|tr|center  — card anchor (default: bl, bottom-left)
 *   ?accent=00E5FF  — hex colour of the visualizer bars (no #)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { wsChatUrl } from "@/lib/api";

const POS_MAP = {
  bl: "bottom-6 left-6 items-start",
  br: "bottom-6 right-6 items-end",
  tl: "top-6 left-6 items-start",
  tr: "top-6 right-6 items-end",
  center: "inset-0 items-center justify-center",
};

const Visualizer = ({ active, accent }) => {
  const bars = 28;
  return (
    <div className="flex items-center gap-1.5 h-20" aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => {
        const base = 10 + ((i * 37) % 60);
        if (!active) {
          return (
            <div
              key={i}
              style={{
                height: 8,
                width: 4,
                borderRadius: 2,
                background: `linear-gradient(180deg, ${accent}, ${accent}55)`,
                opacity: 0.35,
                transition: "height 0.4s ease",
              }}
            />
          );
        }
        return (
          <motion.div
            key={i}
            animate={{ height: [base, base + 40 + Math.random() * 24, base] }}
            transition={{ duration: 0.45 + (i % 5) * 0.08, repeat: Infinity, ease: "easeInOut", delay: i * 0.02 }}
            style={{
              width: 4,
              borderRadius: 2,
              background: `linear-gradient(180deg, ${accent}, ${accent}99 55%, ${accent}22)`,
              boxShadow: `0 0 12px ${accent}66`,
              height: base,
            }}
          />
        );
      })}
    </div>
  );
};

const OverlayOBS = () => {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const scale = Math.min(2, Math.max(0.5, parseFloat(params.get("scale") || "1") || 1));
  const pos = POS_MAP[params.get("pos") || "bl"] || POS_MAP.bl;
  const accent = `#${(params.get("accent") || "00E5FF").replace(/[^0-9A-Fa-f]/g, "").slice(0, 6) || "00E5FF"}`;

  const [current, setCurrent] = useState(null);
  const [active, setActive] = useState(false);
  const wsRef = useRef(null);
  const hideTimer = useRef(null);

  useEffect(() => {
    // Force transparent background for OBS browser source.
    const prev = document.body.style.background;
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    return () => { document.body.style.background = prev; };
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const ws = new WebSocket(wsChatUrl(sessionId));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const p = JSON.parse(ev.data);
        if (p.type === "message" && p.data) {
          setCurrent(p.data);
          setActive(true);
          if (hideTimer.current) clearTimeout(hideTimer.current);
          // Keep card visible for ~7s per message (approximate reading window)
          hideTimer.current = setTimeout(() => setActive(false), 7000);
        }
      } catch (e) { /* noop */ }
    };
    ws.onclose = () => setActive(false);
    return () => {
      try { ws.close(); } catch (e) { /* noop */ }
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [sessionId]);

  const cardStyle = useMemo(() => ({
    transform: `scale(${scale})`,
    transformOrigin: pos.includes("right") ? "right bottom" : "left bottom",
  }), [scale, pos]);

  return (
    <div
      className="fixed inset-0 pointer-events-none select-none"
      style={{ background: "transparent" }}
      data-testid="obs-overlay"
    >
      <div className={`absolute flex flex-col ${pos}`} style={cardStyle}>
        <AnimatePresence>
          {current && (
            <motion.div
              key={current.id || "cur"}
              initial={{ opacity: 0, y: 14, scale: 0.98 }}
              animate={{ opacity: active ? 1 : 0, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 14 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="rounded-2xl px-5 py-4 mb-3 max-w-md"
              style={{
                background: "rgba(5, 5, 10, 0.72)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: `1px solid ${accent}44`,
                boxShadow: `0 10px 40px rgba(0,0,0,0.45), 0 0 24px ${accent}22`,
              }}
            >
              <div
                className="text-[11px] font-semibold tracking-widest uppercase mb-1"
                style={{ color: current.color || accent, letterSpacing: "0.14em" }}
              >
                {current.username || "anon"}
              </div>
              <div className="text-white text-base leading-snug font-medium break-words">
                {current.text}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          className="rounded-2xl px-4 py-3"
          style={{
            background: "rgba(5, 5, 10, 0.55)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: `1px solid ${accent}33`,
          }}
        >
          <Visualizer active={active} accent={accent} />
          <div
            className="text-[9px] uppercase tracking-[0.3em] mt-2 text-center"
            style={{ color: `${accent}cc` }}
          >
            {active ? "Lyra · reading" : "Lyra · idle"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverlayOBS;
